import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Paper,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Email,
  WhatsApp,
  Facebook,
  Instagram,
  LinkedIn,
  LocationOn,
  ArrowBackIosNew,
  CheckCircle,
} from "@mui/icons-material";
import emailjs from "emailjs-com";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useReducedMotion } from "framer-motion";
import MagneticButton from "./ui/MagneticButton";

const SERIF = "'Playfair Display', 'Nunito', serif";

const reasons = [
  "General Query",
  "Free Audit Request",
  "Search Engine Optimization",
  "Pay-Per-Click",
  "Social Media Marketing",
  "Email Marketing",
  "Web Development",
  "Reporting",
  "Web Design",
];

const socialLinks = [
  { icon: <Facebook fontSize="small" />, href: "https://www.facebook.com/people/SLGT-Digital-Marketing-Agency/61572272860427/" },
  { icon: <Instagram fontSize="small" />, href: "https://www.instagram.com/slgt_digital_marketing_agency/" },
  { icon: <LinkedIn fontSize="small" />, href: "https://www.linkedin.com/in/slgt-digital-marketing-agency-396936346/" },
];

export default function QuotationForm() {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const reveal = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-60px" },
          transition: { duration: 0.6, delay, ease: "easeOut" },
        };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setSelectedReasons((prev) =>
      checked ? [...prev, value] : prev.filter((reason) => reason !== value)
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = {
      name: event.target.name.value.trim() || "Not provided",
      email: event.target.email.value.trim() || "Not provided",
      phone: event.target.phone.value.trim() || "Not provided",
      website: event.target.website.value.trim() || "Not provided",
      contact_reasons: selectedReasons.length
        ? selectedReasons.join(", ")
        : "Not specified",
      message: event.target.message.value.trim() || "No message provided",
    };

    emailjs
      .send("service_mzjkjyr", "template_48qb2om", formData, "Dp7yeBGBSaVSYZ0Hx")
      .then(() => {
        setOpenDialog(true);
        setTimeout(() => setOpenDialog(false), 5000);
      })
      .catch((error) => console.error("Error sending email:", error));
  };

  return (
    <>
      <Helmet>
        <title>Request a Quote | SLGT Digital Marketing</title>
        <meta
          name="description"
          content="Get a free quote for SEO, Google Ads, Website Development, SMM, and more from SLGT Digital Marketing Agency in Visakhapatnam."
        />
        <meta
          name="keywords"
          content="Digital Marketing Quotation, SEO Pricing, SMM Quote, SLGT Agency Visakhapatnam, Request a Quote, Marketing Packages"
        />
        <link rel="canonical" href="https://www.slgt.in/requestquotes" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Digital Marketing Quote Request",
            "provider": {
              "@type": "Organization",
              "name": "SLGT Digital",
              "url": "https://www.slgt.in",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "1st Floor, Gandhi Market",
                "addressLocality": "Anakapalle",
                "addressRegion": "Andhra Pradesh",
                "postalCode": "531001",
                "addressCountry": "IN"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-8500501823",
                "contactType": "Customer Support"
              }
            }
          }
        `}</script>
      </Helmet>

      {/* Back Button */}
      <Box
        sx={{
          position: "fixed",
          top: { xs: 50, sm: 70 },
          left: { xs: 12, sm: 24 },
          zIndex: 1200,
          bgcolor: "#FDFBF7",
          border: "1px solid rgba(20,20,31,0.12)",
          borderRadius: "50%",
          width: { xs: 40, sm: 48 },
          height: { xs: 40, sm: 48 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          size="medium"
          sx={{ color: "#14141F", p: 0.5 }}
          aria-label="Go back"
        >
          <ArrowBackIosNew fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ bgcolor: "#FDFBF7", minHeight: "100vh" }}>
        {/* Header */}
        <Container maxWidth="md" sx={{ pt: { xs: 12, sm: 14 }, pb: { xs: 4, sm: 6 }, textAlign: "center" }}>
          <motion.div {...reveal(0)}>
            <Typography
              sx={{
                color: "#FD8112",
                letterSpacing: 3,
                fontWeight: 700,
                fontSize: "0.85rem",
                mb: 2,
                textTransform: "uppercase",
              }}
            >
              Get a Quote
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontFamily: SERIF, fontWeight: 700, mb: 2, fontSize: { xs: "2rem", sm: "2.75rem" } }}
            >
              Let's Scope Your Project
            </Typography>
            <Box sx={{ width: 60, height: 2, bgcolor: "#FD8112", mx: "auto", mb: 3 }} />
            <Typography sx={{ color: "text.secondary", fontSize: "1.05rem", maxWidth: 520, mx: "auto" }}>
              Tell us a little about your business and goals — we'll prepare a
              tailored proposal and get back to you within 24 hours.
            </Typography>
          </motion.div>
        </Container>

        {/* Form + Info */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, sm: 12 } }}>
          <Grid container spacing={4}>
            {/* Form */}
            <Grid item xs={12} md={7}>
              <motion.div {...reveal(0.1)} style={{ height: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{ p: { xs: 3, sm: 4, md: 5 }, height: "100%", border: "1px solid rgba(20,20,31,0.08)" }}
                >
                  <Typography variant="h5" sx={{ fontFamily: SERIF, fontWeight: 700, mb: 3 }}>
                    Tell Us About Your Project
                  </Typography>

                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth name="name" label="Your Name" required />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth name="email" label="Your Email" required />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth name="phone" label="Your Phone" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth name="website" label="Your Website" />
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3.5 }}>
                      <Typography
                        sx={{
                          color: "#FD8112",
                          letterSpacing: 1.5,
                          fontWeight: 700,
                          fontSize: "0.78rem",
                          mb: 1,
                          textTransform: "uppercase",
                        }}
                      >
                        Reason to Contact
                      </Typography>
                      <Grid container spacing={0}>
                        {reasons.map((label) => (
                          <Grid item xs={12} sm={6} key={label}>
                            <FormControlLabel
                              control={<Checkbox value={label} onChange={handleCheckboxChange} />}
                              label={label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="message"
                      label="Your Message"
                      margin="normal"
                    />

                    <MagneticButton
                      type="submit"
                      variant="contained"
                      size="large"
                      sx={{
                        mt: 2,
                        display: "block",
                        mx: "auto",
                        px: 5,
                        bgcolor: "#FD8112",
                        color: "#fff",
                        "&:hover": { bgcolor: "#C85E00" },
                      }}
                    >
                      Request a Quote
                    </MagneticButton>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            {/* Info */}
            <Grid item xs={12} md={5}>
              <motion.div {...reveal(0.2)} style={{ height: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: { xs: 3, sm: 4, md: 5 },
                    bgcolor: "#F4F1EA",
                    border: "1px solid rgba(20,20,31,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h5" sx={{ fontFamily: SERIF, fontWeight: 700, mb: 1.5 }}>
                    Thank You for Your Interest
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                    We're a full-service digital marketing agency based in
                    Anakapalli. Our services span SEO, PPC, social media
                    management, and content creation.
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
                    <LocationOn sx={{ color: "#FD8112", mt: "2px" }} />
                    <Typography color="text.secondary">
                      1st Floor, Dalli Vari Street, Gandhi Market, Anakapalli - 531001
                    </Typography>
                  </Box>

                  <Box
                    component="a"
                    href="mailto:slgtdigitalmarketing@gmail.com"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, color: "text.primary", textDecoration: "none" }}
                  >
                    <Email sx={{ color: "#FD8112" }} />
                    slgtdigitalmarketing@gmail.com
                  </Box>

                  <Box
                    component="a"
                    href="https://wa.me/918500501823"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, color: "text.primary", textDecoration: "none" }}
                  >
                    <WhatsApp sx={{ color: "#FD8112" }} />
                    +91 8500501823
                  </Box>

                  <Divider sx={{ borderColor: "rgba(20,20,31,0.12)", mb: 2.5 }} />

                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Follow Us</Typography>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {socialLinks.map((social) => (
                      <IconButton
                        key={social.href}
                        component="a"
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "#14141F",
                          border: "1px solid rgba(20,20,31,0.15)",
                          "&:hover": { borderColor: "#FD8112", color: "#FD8112" },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Dialog open={openDialog} PaperProps={{ sx: { borderRadius: 2, textAlign: "center", p: 2 } }}>
        <DialogContent>
          <CheckCircle sx={{ fontSize: 48, color: "success.main", mb: 1.5 }} />
          <Typography sx={{ fontFamily: SERIF, fontWeight: 700, fontSize: "1.3rem", mb: 0.5 }}>
            Request Submitted
          </Typography>
          <Typography color="text.secondary">We'll get back to you soon.</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
